"use strict";
// Presentation metadata for the subscription tiers — the single source of the
// plan display order, placeholder prices, and the help article behind each plan
// card on the owner-facing Subscription page.
//
// This is a pure, Firebase-free layer *on top of* the `TIERS`/`resolveFeatures`
// truth in `entitlements.ts` (GEN-004): it never redefines which feature a tier
// unlocks — it only decides how the plans are presented. Prose is resolved via
// Transloco on the client (FE-003), so this file carries i18n KEYS and numbers
// only, never translated strings.
Object.defineProperty(exports, "__esModule", { value: true });
exports.TIER_CATALOG = void 0;
exports.cheapestPlanNameKeyFor = cheapestPlanNameKeyFor;
exports.cheapestTierFor = cheapestTierFor;
exports.cheapestTierWithoutLimit = cheapestTierWithoutLimit;
exports.isTierUpgrade = isTierUpgrade;
const entitlements_js_1 = require("./entitlements.js");
/** Prefix for the per-tier presentation keys (name/description). */
const PLAN_PREFIX = 'subscription.plans.';
function planKey(tier, leaf) {
    return `${PLAN_PREFIX}${tier}.${leaf}`;
}
/**
 * The presentation catalog, cheapest to richest. `free` carries no price
 * (PRD-1807): it is not self-service change-selectable and renders with an
 * "available later / on request" hint instead.
 */
exports.TIER_CATALOG = [
    {
        tierId: 'free',
        order: 0,
        nameKey: planKey('free', 'name'),
        descriptionKey: planKey('free', 'description'),
        helpArticleSlug: 'pricing-free',
    },
    {
        tierId: 'standard',
        order: 1,
        nameKey: planKey('standard', 'name'),
        descriptionKey: planKey('standard', 'description'),
        helpArticleSlug: 'pricing-standard',
    },
    {
        tierId: 'business',
        order: 2,
        nameKey: planKey('business', 'name'),
        descriptionKey: planKey('business', 'description'),
        helpArticleSlug: 'pricing-business',
    },
];
/**
 * The i18n key of the cheapest catalog plan whose matrix column grants
 * `feature` — the "included in <plan>" answer behind every upsell surface.
 *
 * Tier-matrix math, so it lives beside the matrix rather than inside whichever
 * component happens to need it (GEN-004): the in-page upsell placeholder and
 * the app launcher's locked tiles must never name different plans for the same
 * capability. Ordered by `order`, not by declaration order — "cheapest" is the
 * catalog's ranking, not the file's layout.
 *
 * Falls back to the richest catalog plan (highest `order`) for a capability no
 * tier grants. That is NOT a defensive branch: `business` grants every TIER
 * capability, but an APP capability (`aiChat`, `taxAdvisorHandover`,
 * `procurement`, …) is found in no tier at all, and this function then names a
 * plan that does not include it. Surfaces that may show an app capability must
 * ask the billing package's purchase options instead, which report "no tier"
 * for it and name the packages that do grant it (issue #1097).
 */
function cheapestPlanNameKeyFor(feature) {
    return planKey(cheapestTierFor(feature), 'name');
}
/**
 * The cheapest catalog tier whose matrix column grants `feature` — the tier an
 * upsell surface pre-selects when it sends the owner to the subscription page
 * (issue #1089: the locked entry's link carries `?upgrade=<tier>`, and the page
 * lands on the checkout with that tier chosen rather than the current one).
 *
 * Same ranking and same defensive fallback as {@link cheapestPlanNameKeyFor},
 * which is derived from it so the two can never disagree.
 */
function cheapestTierFor(feature) {
    const byOrder = [...exports.TIER_CATALOG].sort((a, b) => a.order - b.order);
    const entry = byOrder.find((e) => (0, entitlements_js_1.resolveFeatures)(e.tierId)[feature]);
    return entry?.tierId ?? byOrder[byOrder.length - 1]?.tierId ?? 'business';
}
/**
 * The cheapest catalog tier on which `limit` is lifted — the tier an upsell for
 * a plan CAP pre-selects (issue #1122), as {@link cheapestTierFor} is for a
 * missing capability. `undefined` when every tier holds the cap.
 */
function cheapestTierWithoutLimit(limit) {
    return [...exports.TIER_CATALOG]
        .sort((a, b) => a.order - b.order)
        .find((e) => (0, entitlements_js_1.resolveLimits)(e.tierId)[limit] === undefined)?.tierId;
}
/**
 * The plan's position in the ladder (free = 0 … business = 2) — the product's
 * own notion of a higher plan, so "up" means what it means on the pricing page.
 * Prices cannot serve: `free` has none.
 */
function tierRank(tier) {
    return exports.TIER_CATALOG.find((entry) => entry.tierId === tier)?.order ?? 0;
}
/**
 * Whether moving from `from` to `to` climbs the ladder. One derivation, read by
 * the page that OFFERS the move and by the classifier that decides whether it
 * applies now or waits for the period end — a page ranking the ladder its own
 * way would offer as immediate what the engine defers.
 */
function isTierUpgrade(from, to) {
    return tierRank(to) > tierRank(from);
}
//# sourceMappingURL=tier-catalog.js.map